import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  Signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  Channel,
  DoddleState,
  EPostState,
  EDoddleState,
  extractChannelHandle,
} from '@doddly/core';
import { FormGroup } from '@angular/forms';
import { ChannelAvatarComponent } from '../channel-avatar/channel-avatar.component';
import { IconComponent } from '../icon/icon.component';
import { TooltipModule } from 'primeng/tooltip';
@Component({
  selector: 'ly-channel-picker',
  standalone: true,
  imports: [
    CommonModule,
    ChannelAvatarComponent,
    IconComponent,
    TooltipModule,
  ],
  templateUrl: './channel-picker.component.html',
  styleUrl: './channel-picker.component.scss',
})
export class ChannelPickerComponent implements OnChanges {
  @Input() channels: Channel[] = [];
  @Input() toggledChannel: Channel | null = null;
  @Input() value: Channel[] = [];
  @Input() posts!: Signal<FormGroup[]>;
  @Input() disabled = false;
  @Input() doddleStatus!: DoddleState | undefined;
  @Input() selectedChannel!: Channel | null;
  @Input() forcedChannelIds: string[] = [];

  @Output() activeChannel = new EventEmitter<Channel>();
  @Output() valueChange = new EventEmitter<Channel>();

  readonly EDoddleState = EDoddleState;

  ngOnChanges(): void {
    // preselect first active channel
    if (this.channels.length > 0 && this.value.length > 0) {
      if (this.selectedChannel) {
        this.selectedChannel =
          this.channels.find((c) => c.id === this.selectedChannel?.id) || null;
        this.activeChannel.emit(this.selectedChannel!);
      } else {
        this.activeChannel.emit(this.value[0]);
        this.selectedChannel = this.value[0];
      }
    }
  }

  isToggled(channel: Channel): boolean {
    return this.toggledChannel?.id === channel.id;
  }

  onChannelClick(channel: Channel) {
    this.selectedChannel = channel;
    this.activeChannel.emit(channel);
  }

  onChannelSelectionChange(channel: Channel) {
    this.valueChange.emit(channel);
    // Reset toggledChannel after handling the toggle
    if (this.toggledChannel?.id === channel.id) {
      this.toggledChannel = null;
    }
  }

  isSelected(channel: Channel): boolean {
    if (!this.posts().length) return false;

    const isInValue = this.value.findIndex((c) => c.id === channel.id) !== -1;
    const post = this.getPostsByChannel(channel);
    const isNotPaused = post?.get('status')?.value !== EPostState.paused;
    return isInValue && isNotPaused;
  }

  isForced(channel: Channel): boolean {
    return this.forcedChannelIds.includes(channel.id);
  }

  hasValidationErrors(channel: Channel): boolean {
    const post = this.getPostsByChannel(channel);
    const isPaused = post?.get('status')?.value === EPostState.paused;
    return post?.get('validationErrors')?.value?.length > 0 && !isPaused;
  }

  isChannelDisabled(channel: Channel): boolean {
    const post = this.getPostsByChannel(channel);
    return post?.get('status')?.value === EPostState.paused;
  }

  isPostPublished(channel: Channel): boolean {
    const post = this.getPostsByChannel(channel);
    return post?.get('status')?.value === EPostState.published;
  }

  isPostFailed(channel: Channel): boolean {
    const post = this.getPostsByChannel(channel);
    return post?.get('status')?.value === EPostState.failed;
  }

  postExternalLink(channel: Channel): string {
    const post = this.getPostsByChannel(channel);
    return post?.get('externalURL')?.value;
  }

  getLastStatusMessage(channel: Channel): string | null {
    const post = this.getPostsByChannel(channel);

    if (!post) {
      return null;
    }

    const statusMessages = post.get('statusMessages')?.value;
    if (!statusMessages || statusMessages.length === 0) {
      return null;
    }

    const lastMessage = statusMessages[statusMessages.length - 1];

    return this.formatStatusMessage(lastMessage);
  }

  extractChannelHandle(channel: Channel): string {
    return extractChannelHandle(channel.subscriblyChannel?.URL ?? '');
  }

  private getPostsByChannel(channel: Channel): FormGroup | undefined {
    return this.posts().find(
      (p) => p.get('channelId')?.value === channel.subscriblyChannelId,
    );
  }

  private formatStatusMessage(message: string): string {
    if (!message) return '';

    // Extract timestamp and format it
    const timestampMatch = message.match(
      /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z)/,
    );

    if (timestampMatch) {
      const timestamp = new Date(timestampMatch[1]).toLocaleString();
      const restOfMessage = message.replace(`${timestampMatch[0]}:`, '').trim();
      return `${timestamp}\n${restOfMessage}`;
    }

    return message;
  }
}
